import Swiper from 'swiper';
import { A11y, Autoplay, Navigation, Pagination, EffectFade } from 'swiper/modules';

interface HeroCarouselProgress {
  percent: number;
  interval: NodeJS.Timeout | null;
}

class HeroCarousel {
  private heroCarouselElement: HTMLElement | null;
  private swiperInstance: Swiper | null = null;
  private autoplayTime: number;
  private activeDotColor: string;
  private defaultDotColor: string;
  private isPaused: boolean;
  private progress: HeroCarouselProgress;
  private progressInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeElements();
    this.initializeColors();
    this.initializeCarousel();
    this.setupEventListeners();
  }

  private initializeElements(): void {
    this.heroCarouselElement = document.querySelector('.carousel__container__hero') as HTMLElement;
    
    if (!this.heroCarouselElement) return;
    
    const autoplayData = this.heroCarouselElement.dataset.autoplay;
    this.autoplayTime = autoplayData && Number(autoplayData) > 0 ? Number(autoplayData) : 5;
  }

  private initializeColors(): void {
    this.activeDotColor = 
      getComputedStyle(document.documentElement).getPropertyValue('--brand-color-1').trim() || '#ff0000';
    this.defaultDotColor = 
      getComputedStyle(document.documentElement).getPropertyValue('--hero-header-carousel-bg-dots').trim() || '#cccccc';
  }

  private initializeCarousel(): void {
    if (!this.heroCarouselElement) return;

    this.convertToSwiperStructure();

    this.isPaused = false;
    this.progress = {
      percent: 0,
      interval: null
    };

    this.swiperInstance = new Swiper(this.heroCarouselElement, {
      modules: [A11y, Autoplay, Navigation, Pagination, EffectFade],
      spaceBetween: 0,
      autoplay: false,
      // Slides advance on our own timer, which Swiper's live region can't detect.
      a11y: {
        wrapperLiveRegion: false,
      },
      effect: "fade",
      loop: false,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: false
      },
      on: {
        init: () => this.onSwiperInit(),
        slideChange: () => this.onSlideChange(),
        touchStart: () => this.pauseProgress(),
        touchEnd: () => this.resumeProgress()
      }
    });
  }

  private convertToSwiperStructure(): void {
    if (!this.heroCarouselElement) return;

    this.heroCarouselElement.classList.remove('slick-initialized', 'slick-slider', 'slick-dotted');
    this.heroCarouselElement.classList.add('swiper');

    const slides = this.heroCarouselElement.querySelectorAll('.carousel__item');
    const swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper-wrapper';

    slides.forEach((slide: Element) => {
      (slide as HTMLElement).classList.add('swiper-slide');
      swiperWrapper.appendChild(slide);
    });

    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'swiper-button-prev swiper-button';

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'swiper-button-next swiper-button';

    const pagination = document.createElement('div');
    pagination.className = 'swiper-pagination hero-carousel-pagination';
    
    this.heroCarouselElement.innerHTML = '';
    this.heroCarouselElement.appendChild(swiperWrapper);
    this.heroCarouselElement.appendChild(prevButton);
    this.heroCarouselElement.appendChild(nextButton);
    this.heroCarouselElement.appendChild(pagination);
  }

  private onSwiperInit(): void {
    this.startDotProgress();
    this.setupDotsHandlers();
    this.setupNavigationArrows();
  }

  private onSlideChange(): void {
    this.resetDotProgress();
    this.startDotProgress();
  }

  private setupEventListeners(): void {
    const hoverElements = document.querySelectorAll('.carousel__container__hero, .tour-search-form');
    
    hoverElements.forEach(element => {
      element.addEventListener('mouseenter', () => {
        this.isPaused = true;
      });
      element.addEventListener('mouseleave', () => {
        this.isPaused = false;
      });
    });
  }

  private setupDotsHandlers(): void {
    const dots = document.querySelectorAll('.hero-carousel-pagination .swiper-pagination-bullet');
    if (dots.length > 0) {
      dots.forEach(dot => {
        dot.removeEventListener('click', this.handleDotClick);
        dot.addEventListener('click', this.handleDotClick.bind(this));
      });
    }
  }

  private setupNavigationArrows(): void {
    const prevButton = document.querySelector('.swiper-button.swiper-button-prev');
    const nextButton = document.querySelector('.swiper-button.swiper-button-next');

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.resetDotProgress();
        this.startDotProgress();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.resetDotProgress();
        this.startDotProgress();
      });
    }
  }

  private handleDotClick(): void {
    this.resetDotProgress();
    this.startDotProgress();

    if (document.querySelector('.carousel__container__hero:hover, .tour-search-form:hover')) {
      this.isPaused = true;
    }
  }

  public startDotProgress(): void {
    this.resetDotProgress();
    this.progress.percent = 0;
    this.isPaused = false;
    this.progressInterval = setInterval(() => this.updateDotProgress(), 10);
  }

  private updateDotProgress(): void {
    if (!this.isPaused) {
      const increment = 100 / (this.autoplayTime * 1000 / 10);
      this.progress.percent += increment;
      
      const activeDot = document.querySelector('.hero-carousel-pagination .swiper-pagination-bullet-active') as HTMLElement;
      if (activeDot) {
        activeDot.style.background = 
          `linear-gradient(to right, ${this.activeDotColor} ${this.progress.percent}%, ${this.defaultDotColor} ${this.progress.percent}%)`;
      }

      if (this.progress.percent >= 100) {
        this.goToNextSlide();
        this.startDotProgress();
      }
    }
  }

  private goToNextSlide(): void {
    const swiper = this.swiperInstance;
    if (!swiper) return;

    if (swiper.isEnd) {
      swiper.slideTo(0);
      return;
    }

    swiper.slideNext();
  }

  private pauseProgress(): void {
    this.isPaused = true;
  }

  private resumeProgress(): void {
    this.isPaused = false;
  }

  private resetDotProgress(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    document.querySelectorAll('.hero-carousel-pagination .swiper-pagination-bullet').forEach(dot => {
      (dot as HTMLElement).style.background = this.defaultDotColor;
    });
  }

  public destroy(): void {
    this.resetDotProgress();
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }
  }
}

export function initHeroCarousel(): void {
  new HeroCarousel();
}

function checkAndInitialize(): void {
  const heroCarousel = document.querySelector('.carousel__container__hero');
  
  if (heroCarousel) {
    initHeroCarousel();
  }
}

function initializeWhenReady(): void {
  const heroCarousel = document.querySelector('.carousel__container__hero');
  
  if (!heroCarousel) return;
  
  const slides = heroCarousel.querySelectorAll('div');
  
  if (slides.length > 0) {
    initHeroCarousel();
  } else {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any div elements were added
          const hasSlides = Array.from(mutation.addedNodes).some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            ((node as Element).tagName === 'DIV' || (node as Element).querySelector('div'))
          );
          
          if (hasSlides) {
            observer.disconnect();
            initHeroCarousel();
          }
        }
      });
    });
    
    observer.observe(heroCarousel, {
      childList: true,
      subtree: true
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWhenReady);
} else {
  initializeWhenReady();
}

export default HeroCarousel;
